import { getColors } from '../../charts/shared/palettes';
import { J, JN, TITLE_SPACING, fileHeader } from './shared';

export function genHistogram(chartType, config, t, data) {
  // data is an array of raw values
  const numBins = config.numBins || 12;
  const mn = Math.min(...data);
  const mx = Math.max(...data);
  const binWidth = (mx - mn) / numBins;
  const bins = Array.from({ length: numBins }, (_, i) => {
    const binStart = mn + i * binWidth;
    const binEnd = mn + (i + 1) * binWidth;
    let count = 0;
    data.forEach(v => {
      const bi = Math.min(Math.floor((v - mn) / binWidth), numBins - 1);
      if (bi === i) count++;
    });
    return {
      label: `${Math.round(binStart)}-${Math.round(binEnd)}`,
      count,
    };
  });

  const barColor = getColors(config, 1, t)[0];
  const showGrid = !!config.showGrid;
  const showEdges = !!config.showEdges;
  const hasTitle = !!(config.title && String(config.title).trim());
  const topMargin = hasTitle ? 40 : 20;

  const maxCount = Math.max(...bins.map(b => b.count));
  const yDomMin = (config.yMin && String(config.yMin).trim() !== '') ? parseFloat(config.yMin) : 0;
  const yDomMax = (config.yMax && String(config.yMax).trim() !== '') ? parseFloat(config.yMax) : Math.ceil(maxCount * 1.1);

  return (
    fileHeader(['@visx/scale', '@visx/shape', '@visx/axis', '@visx/grid', '@visx/group'], 'histogram') +
    `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';

const BINS = ${JN(bins)};
const BAR_COLOR = ${J(barColor)};
const THEME = ${JN(t)};
const Y_DOMAIN = [${yDomMin}, ${yDomMax}];

export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${topMargin}, right: 80, bottom: 60, left: 70 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const xScale = useMemo(() => scaleBand({
    domain: BINS.map(d => d.label),
    range: [0, innerWidth],
    padding: 0,
  }), [innerWidth]);

  const yScale = useMemo(() => scaleLinear({
    domain: Y_DOMAIN,
    range: [innerHeight, 0],
  }), [innerHeight]);

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>
      ${hasTitle ? `<div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 70, fontSize: 18, fontWeight: 400, color: THEME.titleColor }}>${String(config.title).replace(/</g, '&lt;')}</div>` : ''}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          ${showGrid ? `<GridRows scale={yScale} width={innerWidth} strokeDasharray="3 3" stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} />` : ''}
          {BINS.map((bin) => {
            const barX = xScale(bin.label) ?? 0;
            const barW = xScale.bandwidth();
            const barY = yScale(bin.count);
            const barH = innerHeight - barY;
            if (barH <= 0) return null;
            return (
              <Bar
                key={bin.label}
                x={barX}
                y={barY}
                width={barW}
                height={barH}
                fill={BAR_COLOR}
                stroke={${showEdges ? 'THEME.foreground' : J('none')}}
                strokeWidth={${showEdges ? 1 : 0}}
              />
            );
          })}
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            stroke={THEME.axisColor}
            strokeWidth={THEME.spineWidth}
            tickStroke={THEME.axisColor}
            tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 9, textAnchor: 'end', transform: 'rotate(-45)', dx: '-0.25em', dy: '0.25em' })}
            label={${J(config.xlabel || '')}}
            labelOffset={30}
            labelProps={{ fill: THEME.labelColor, fontSize: 14, textAnchor: 'middle' }}
          />
          <AxisLeft
            scale={yScale}
            stroke={THEME.axisColor}
            strokeWidth={THEME.spineWidth}
            tickStroke={THEME.axisColor}
            tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 11, textAnchor: 'end', dx: '-0.25em', dy: '0.33em' })}
            label={${J(config.ylabel || '')}}
            labelOffset={45}
            labelProps={{ fill: THEME.labelColor, fontSize: 14, textAnchor: 'middle' }}
          />
        </Group>
      </svg>
    </div>
  );
}
`
  );
}
